import { useState, useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { Alert as RNAlert, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { ProductImage, TemplateFile, Classification, GeneratedJson } from '../types/LandingPageTypes';
import { extractHtmlFromElementorJson } from '../utils/elementorUtils';
import axios, { CancelTokenSource } from 'axios';
import axiosInstance from '../services/axiosInstance';
import { translatePatterns } from '../utils/commands';
import { useAuth } from './useAuth';
import { useSettings } from './useSettings';
import { COLORS } from '../constants/colors';

import { toast, ToastContentProps } from 'react-toastify';
import { trackEvent, trackException } from '../utils/appInsights';
import { mixpanelTrack } from '../utils/mixpanel';
import { useFeatureFlag } from '../featureFlags/FeatureFlagsContext';

const forbiddenToastStyles = StyleSheet.create({
  wrapper: {
    width: '100%',
    flexDirection: 'row',
    backgroundColor: '#fff',
  },
  body: {
    flex: 1,
    padding: 16,
    gap: 6,
  },
  message: {
    fontSize: 14,
    color: '#4b5563',
    lineHeight: 20,
  },
  divider: {
    width: 2,
    backgroundColor: COLORS.BRAND_PRIMARY,
  },
  actions: {
    width: 110,
  },
  actionButton: {
    width: '100%',
    height: 44,
    alignItems: 'center',
    justifyContent: 'center',
  },
  settingsButton: {
    backgroundColor: COLORS.BRAND_PRIMARY,
  },
  settingsText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  closeText: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '600',
  },
  rowDivider: {
    height: 2,
    backgroundColor: COLORS.BRAND_PRIMARY,
  },
});

function showAlert(title: string, message: string) {
  if (Platform.OS === 'web') {

    toast.error(message, {
      position: "top-center",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      });
  } else {
    RNAlert.alert(title, message);
  }
}

type ForbiddenToastProps = ToastContentProps & {
  message: unknown;
  onOpenSettings: () => void;
};

function ForbiddenToast({ closeToast, message, onOpenSettings }: ForbiddenToastProps) {
  const safeMessage = (() => {
    if (typeof message === 'string') return message;
    if (message == null) return "";
    if (typeof message === 'object') {
      const anyMessage = message as any;
      const detail = anyMessage?.detail;
      const msg = anyMessage?.message;
      const title = anyMessage?.title;
      if (typeof detail === 'string' && detail.trim()) return detail;
      if (typeof msg === 'string' && msg.trim()) return msg;
      if (typeof title === 'string' && title.trim()) return title;

      try {
        return JSON.stringify(message);
      } catch {
        return String(message);
      }
    }
    return String(message);
  })();

  return (
    <View style={forbiddenToastStyles.wrapper}>
      <View style={forbiddenToastStyles.body}>
        <Text style={forbiddenToastStyles.message}>{safeMessage}</Text>
      </View>

      <View style={forbiddenToastStyles.divider} />

      <View style={forbiddenToastStyles.actions}>
        <TouchableOpacity
          style={[forbiddenToastStyles.actionButton, forbiddenToastStyles.settingsButton]}
          onPress={() => {
            onOpenSettings();
            closeToast?.('settings');
          }}
          accessibilityRole="button"
        >
          <Text style={forbiddenToastStyles.settingsText}>Settings</Text>
        </TouchableOpacity>

        <View style={forbiddenToastStyles.rowDivider} />

        <TouchableOpacity
          style={forbiddenToastStyles.actionButton}
          onPress={() => closeToast?.('close')}
          accessibilityRole="button"
        >
          <Text style={forbiddenToastStyles.closeText}>Close</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const progressMessages = [
  "I'm checking your Landingator license...",
  "I'm analyzing the template and discovering all its secrets....",
  "I'm extracting persuasive keywords...",
  "I'm creating engaging content for your product...",
  "I'm writing sentences that will turn visitors into customers...",
  "I'm optimizing the call-to-action for exceptional performance...",
  "I'm applying the best advanced copywriting techniques...",
  "Your landing page is taking shape...",
  "I'm adding that touch of magic that makes it sell...",
  "I'm highlighting the strengths of your product...",
  "I'm working on the texts to make them irresistible...",
  "A little patience, perfection takes time...",
  "Think about how many conversions you will have with this landing!",
  "I'm creating creative and persuasive content...",
  "The magic is happening, hold on a little longer...",
  "Your product will shine like never before.",
  "I'm calibrating the words to maximize sales...",
  "A pinch of creativity, a pinch of psychology...",
  "I'm composing sentences that will increase your sales...",
  "We've come this far, there's really not much left...",
  "Your landing page will be ready shortly."
];

type RecentWordPressTemplate = {
  id: number | string;
  title: string;
  date?: string;
  slug?: string;
  data?: unknown;
  content?: unknown;
};

export function normalizeWordPressElementorData(data: unknown): unknown[] {
  let parsed = data;
  for (let depth = 0; depth < 3 && typeof parsed === 'string'; depth += 1) {
    parsed = JSON.parse(parsed);
  }

  if (Array.isArray(parsed)) {
    return parsed;
  }

  if (parsed && typeof parsed === 'object') {
    const wrapped = parsed as { content?: unknown; elements?: unknown; data?: unknown };
    for (const candidate of [wrapped.content, wrapped.elements, wrapped.data]) {
      if (candidate !== undefined) {
        return normalizeWordPressElementorData(candidate);
      }
    }

    const numericEntries = Object.entries(parsed as Record<string, unknown>)
      .filter(([key]) => /^\d+$/.test(key))
      .sort(([left], [right]) => Number(left) - Number(right))
      .map(([, value]) => value);
    if (numericEntries.length > 0) return numericEntries;
  }

  throw new Error('WordPress template does not contain valid Elementor data.');
}

export function useLandingPage() {
  const [templateFile, setTemplateFile] = useState<TemplateFile | null>(null);
  const [productDescription, setProductDescription] = useState('');
  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState('');
  const [currencySymbolPosition, setCurrencySymbolPosition] = useState<'left' | 'right'>('right');
  const [productImages, setProductImages] = useState<ProductImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [generatedHtml, setPreviewHtml] = useState('');
  const [generatedJson, setGeneratedJson] = useState<GeneratedJson | null>(null);
  const [regenerableImageUrls, setRegenerableImageUrls] = useState<string[]>([]);
  const [regeneratedImageUrls, setRegeneratedImageUrls] = useState<string[]>([]);
  const [acceptedImageRegenerationCount, setAcceptedImageRegenerationCount] = useState(0);
  const [keywords, setKeywords] = useState<Record<string, string[]>>({});
  const [productClassification, setProductClassification] = useState<Classification | null>(null);
  const [progress, setProgress] = useState(0);
  const [progressStatus, setProgressStatus] = useState('Ready');
  const [skipImageGeneration, setSkipImageGeneration] = useState(false);
  const [skipWidgets, setSkipWidgets] = useState(false);
  const [publishToWordPress, setPublishToWordPress] = useState(false);
  const [wordPressConfigured, setWordPressConfigured] = useState(false);
  const [recentWordPressTemplates, setRecentWordPressTemplates] = useState<RecentWordPressTemplate[]>([]);
  const [selectedWordPressTemplateId, setSelectedWordPressTemplateId] = useState<string | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [fromScratchMode, setFromScratchMode] = useState(false);
  const generationFromScratchEnabled = useFeatureFlag('generationFromScratchEnabled');
  const extraCreditPurchaseEnabled = useFeatureFlag('extraCreditPurchaseEnabled');
  const scratchModeActive = fromScratchMode && generationFromScratchEnabled;
  // Meta Ads product info extraction states
  const [extractingProductInfo, setExtractingProductInfo] = useState(false);
  const [extractProductInfoError, setExtractProductInfoError] = useState<string | null>(null);
  // Meta Ads individual content generation states
  const [generatingMetaMainTexts, setGeneratingMetaMainTexts] = useState(false);
  const [generatingMetaHeadlines, setGeneratingMetaHeadlines] = useState(false);
  const [generatingMetaDescriptions, setGeneratingMetaDescriptions] = useState(false);
  const [metaAdsError, setMetaAdsError] = useState<string | null>(null);
  // Google Ads individual content generation states
  const [generatingGoogleTitles, setGeneratingGoogleTitles] = useState(false);
  const [generatingGoogleLongTitles, setGeneratingGoogleLongTitles] = useState(false);
  const [generatingGoogleDescriptions, setGeneratingGoogleDescriptions] = useState(false);
  const [generatingGoogleSearchTerms, setGeneratingGoogleSearchTerms] = useState(false);
  const [googleAdsError, setGoogleAdsError] = useState<string | null>(null);
  const jsonInputRef = useRef<HTMLInputElement | null>(null);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const abortControllerRef = useRef<CancelTokenSource | null>(null);
  const { token } = useAuth();
  const { settings } = useSettings();

  useEffect(() => {
    if (!token) {
      setWordPressConfigured(false);
      setPublishToWordPress(false);
      return;
    }

    let active = true;
    axiosInstance.get<{ configured: boolean; templatesConfigured: boolean }>('/api/wordpress/status')
      .then(async ({ data }) => {
        if (!active) return;
        const configured = data.configured === true;
        setWordPressConfigured(configured);
        if (!data.templatesConfigured) {
          setRecentWordPressTemplates([]);
          return;
        }

        const recentResponse = await axiosInstance.get<{
          success: boolean;
          templates: RecentWordPressTemplate[];
        }>('/api/wordpress/recent-templates');
        if (active) {
          const templates = Array.isArray(recentResponse.data.templates)
            ? recentResponse.data.templates
            : [];
          setRecentWordPressTemplates(templates);
          setSelectedWordPressTemplateId(
            templates[0]?.id === undefined ? null : String(templates[0].id),
          );
        }
      })
      .catch(() => {
        if (active) setRecentWordPressTemplates([]);
      });

    return () => {
      active = false;
    };
  }, [token, showSettings]);

  // Track the index of the cycling message
  const [cycleIndex, setCycleIndex] = useState(0);

  useEffect(() => {
    // let interval: NodeJS.Timeout;
let interval: ReturnType<typeof setInterval>;
    if (loading) {
      // Cycle through messages every 8 seconds
      interval = setInterval(() => {
        setCycleIndex((prev) => (prev + 1) % progressMessages.length);
      }, 8000);
    } else {
      setCycleIndex(0);
    }

    return () => clearInterval(interval);
  }, [loading]);

  // Never allow scratch mode when the server says it's disabled.
  useEffect(() => {
    if (!generationFromScratchEnabled && fromScratchMode) {
      setFromScratchMode(false);
    }
  }, [generationFromScratchEnabled, fromScratchMode]);

  // --- Global axios interceptor to show alerts on any error ---
  useEffect(() => {
    const id = axiosInstance.interceptors.response.use(
      res => res,
      err => {
        
        if (!axios.isCancel(err) && err.response) {

          handleApiError(err);
          // const detail =
          //   err.response.data?.detail ||
          //   err.response.data?.message ||
          //   `Request failed with status ${err.response.status}`;
          // showAlert('Error', detail);
        
            setProgress(0);
        }
        
        return Promise.reject(err);
      }
    );


    return () => {
      axiosInstance.interceptors.response.eject(id);
    };
  }, []);

  const showForbiddenToast = (message: string) => {
    const toastMessage = message || "You don't have permission to access this resource.";

    toast(
      props => (
        <ForbiddenToast
          {...props}
          message={toastMessage}
          onOpenSettings={() => setShowSettings(true)}
        />
      ),
      {
        closeButton: false,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        position: 'top-center',
        style: {
          padding: 0,
          width: 400,
          border: `2px solid ${COLORS.BRAND_PRIMARY}`,
          boxShadow: '0 10px 24px rgba(17, 24, 39, 0.12)',
          borderRadius: 10,
          overflow: 'hidden',
        },
        ariaLabel: 'Access denied',
      }
    );
  };

  function handleApiError(err: any) {
  const status = err.response.status;
  const url: string = err?.config?.url ?? '';

  const getErrorMessage = (fallback: string) => {
    const data = err?.response?.data;
    if (typeof data === 'string' && data.trim()) return data;

    const detail = data?.detail;
    const message = data?.message;
    const title = data?.title;

    if (typeof detail === 'string' && detail.trim()) return detail;
    if (typeof message === 'string' && message.trim()) return message;
    if (typeof title === 'string' && title.trim()) return title;

    return fallback;
  };

  if (url.includes('/api/landingpage/generateStructure') && status >= 500) {
    showAlert(
      'Template Generation Error',
      'There was an error generating your template. Please try again.'
    );
    return;
  }

  if (url.includes('/api/landingpage/translate') && status == 502) {
    showAlert(
      'Contact support',
      'There was no content returned, likely due to content filtering (a content category that is not supported as per the model terms and conditions). Please contact support to resolve this issue.'
    );
    return;
  }

  if (url.includes('/api/landingpage/generate') && status == 502) {
    showAlert(
      'Contact support',
      'There was no content returned, likely due to content filtering (a content category that is not supported as per the model terms and conditions). Please contact support to resolve this issue.'
    );
    return;
  }

  switch (status) {
    // --- 4xx Client Errors ---
    case 400:
      showAlert('Oops!', "Your request contained an error. Please check your input and try again.");
      break;
    case 401:
      showAlert('Session Expired', "You need to be logged in to do that. Please sign in again.");
      break;
    case 402:
      {
        const detail = getErrorMessage(
          extraCreditPurchaseEnabled
            ? "You've run out of credits. Purchase more to continue generating."
            : "You've run out of credits. Please upgrade your plan to continue generating."
        );

        if (Platform.OS === 'web') {
          showForbiddenToast(detail);
        } else {
          showAlert('Credits Exhausted', detail);
        }
      }
      break;
    case 403:
      {
        const detail = getErrorMessage("You don't have permission to access this resource.");

        if (Platform.OS === 'web') {
          showForbiddenToast(detail);
        } else {
          showAlert('Access Denied', detail);
        }
      }
      break;
    case 404:
      showAlert('Not Found', "The page or resource you're looking for doesn't exist. It may have been moved or deleted.");
      break;
    case 408:
      showAlert('Request Timeout', "The request took too long. Please check your connection and try again.");
      break;
    case 409:
      showAlert('Conflict', "This action conflicts with our current data. Please refresh the page and try again.");
      break;
    case 422:
      showAlert('File Rejected', getErrorMessage('An uploaded file could not be accepted. Please choose another file.'));
      break;
    case 429:
      showAlert('Too Many Requests', "You've been making requests a little too quickly. Please slow down and try again in a moment.");
      break;

    // --- 5xx Server Errors ---
    case 500:
      showAlert('Server Error', "Something went wrong on our servers. Our team has been alerted. Please try again later.");
      break;
    case 502:
      showAlert('Bad Gateway', "Our servers are temporarily down or being maintained. Please try again in a few minutes.");
      break;
    case 503:
      showAlert('File Scan Unavailable', getErrorMessage('An uploaded file could not be verified. Please try again soon.'));
      break;
    case 504:
      showAlert('Gateway Timeout', "One of our servers is taking too long to respond. This is usually temporary. Please try again.");
      break;

    // --- Catch-All for Unknown Errors ---
    default:
      if (status >= 400 && status < 500) {
        showAlert('Request Error', `An unexpected error (${status}) occurred with your request. Please try again.`);
      } else if (status >= 500) {
        showAlert('Server Error', `Our servers are experiencing issues (Error ${status}). Please try again later.`);
      } else {
        showAlert('Error', "An unexpected network error occurred. Please check your connection and try again.");
      }
  }
}
  // Update progressStatus to the cycling message during loading
  useEffect(() => {
    if (loading) {
      setProgressStatus(progressMessages[cycleIndex]);
    }
  }, [cycleIndex, loading]);

  useEffect(() => {
  return () => {
    abortControllerRef.current?.cancel()
  
  }
}, [])

  const pickTemplateFile = async () => {
    jsonInputRef.current?.click();
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const file = files[0];
    if (file.type === 'application/json') {
      const fileUrl = URL.createObjectURL(file);
      setTemplateFile({ uri: fileUrl, name: file.name, type: file.type, file });

      setGeneratedJson(null);

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const json = JSON.parse(reader.result as string);
          const rawHtml = extractHtmlFromElementorJson(json);
          setPreviewHtml(rawHtml);
          setRegenerableImageUrls([]);
          setRegeneratedImageUrls([]);
          setAcceptedImageRegenerationCount(0);
          
          // Track template upload success
          trackEvent('Template_Upload_Success', {
            fileName: file.name,
            fileSize: file.size
          });
        } catch (err) {
          console.error('Error parsing uploaded JSON:', err);
          setPreviewHtml('<p style="color:#ff0000;">Invalid JSON structure.</p>');
          
          // Track template upload error
          trackEvent('Template_Upload_Failed', {
            fileName: file.name,
            error: 'Invalid JSON structure'
          });
          if (err instanceof Error) {
            trackException(err, { context: 'templateUpload' });
          }
        }
      };
      reader.readAsText(file);
    }
  };

  const loadSelectedWordPressTemplate = (templateId = selectedWordPressTemplateId) => {
    const selectedTemplate = recentWordPressTemplates.find(
      template => String(template.id) === String(templateId),
    );
    if (!selectedTemplate) {
      console.error('Selected WordPress template was not found.', {
        selectedTemplateId: templateId,
        availableTemplateIds: recentWordPressTemplates.map(template => String(template.id)),
      });
      showAlert('WordPress Template Error', 'The selected template could not be found. Refresh the page and try again.');
      return;
    }

    try {
      const content = normalizeWordPressElementorData(
        selectedTemplate.data ?? selectedTemplate.content,
      );
      const templateJson = {
        title: selectedTemplate.title,
        type: 'page',
        version: '0.4',
        content,
      };
      const serialized = JSON.stringify(templateJson, null, 2);
      const file = new File([serialized], `wordpress-${selectedTemplate.id}.json`, {
        type: 'application/json',
      });
      const fileUrl = URL.createObjectURL(file);
      const previewHtml = extractHtmlFromElementorJson(templateJson);
      const hasRenderableContent = !previewHtml.includes('No renderable content found') &&
        !previewHtml.includes('Invalid Elementor JSON');

      console.info('WordPress template preview decoded', {
        templateId: selectedTemplate.id,
        rootElementCount: content.length,
        renderedHtmlLength: previewHtml.length,
        hasRenderableContent,
      });

      setTemplateFile({ uri: fileUrl, name: file.name, type: file.type, file });
      setGeneratedJson(null);
      setPreviewHtml(previewHtml);
      setRegenerableImageUrls([]);
      setRegeneratedImageUrls([]);
      setAcceptedImageRegenerationCount(0);
      trackEvent('WordPress_Template_Loaded', { templateId: selectedTemplate.id });

      if (!hasRenderableContent) {
        showAlert(
          'WordPress Template Preview',
          `The template loaded ${content.length} root element(s), but none use Elementor widgets supported by this preview. You can still use it for generation or translation.`,
        );
      }
    } catch (error) {
      console.error('Failed to load WordPress template:', error);
      showAlert('WordPress Template Error', 'The selected template does not contain valid Elementor data.');
    }
  };

  const handleImagePick = async () => {
    imageInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const maxImages = 3;
      const availableSlots = maxImages - productImages.length;
      const filesToAdd = Array.from(files).slice(0, availableSlots);
      
      const newImages: ProductImage[] = filesToAdd.map(file => ({
        name: file.name,
        uri: URL.createObjectURL(file),
        type: file.type,
        file
      }));
      
      setProductImages(prev => [...prev, ...newImages]);
      
      // Track image upload
      trackEvent('Product_Images_Upload', {
        count: newImages.length,
        totalImages: productImages.length + newImages.length
      });
      
      // Reset input
      if (imageInputRef.current) imageInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setProductImages(prev => prev.filter((_, i) => i !== index));
    
    // Track image removal
    trackEvent('Product_Image_Removed', {
      remainingImages: productImages.length - 1
    });
  };

  const handleExportKeywords = () => {
    if (Object.keys(keywords).length === 0) return;
    const json = JSON.stringify(keywords, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'keywords.json';
    a.click();
    
    // Track keywords export
    trackEvent('Keywords_Exported', {
      keywordCount: Object.keys(keywords).length
    });
  };

  // Extract product information for Meta Ads pane.
  const handleExtractProductInfo = async (setProductInfo: (info: string) => void, adsEnum: string) => {
    if (extractingProductInfo) return; // prevent concurrent calls
    setExtractProductInfoError(null);
    setExtractingProductInfo(true);

    try {
      if (!token) {
        throw new Error('Not authenticated');
      }

      const formData = new FormData();
      formData.append('adsEnum', adsEnum);

      const generatedPayload = generatedJson && (generatedJson as any).generatedJson
        ? (generatedJson as any).generatedJson
        : generatedJson;

      if (generatedPayload) {
        const generatedBlob = new Blob([JSON.stringify(generatedPayload, null, 2)], { type: 'application/json' });
        formData.append('templateJson', generatedBlob, 'generated-template.json');
      } else if (templateFile?.file) {
        formData.append('templateJson', templateFile.file, templateFile.name);
      } else {
        showAlert('Error', 'Template file or generated content is required.');
        setExtractingProductInfo(false);
        return;
      }

      // Track extraction start
      trackEvent('Product_Info_Extraction_Started', {
        adsType: adsEnum,
        source: generatedPayload ? 'generated' : 'uploaded'
      });

      const response = await axiosInstance.post(
      '/api/landingpage/extract',
      formData,
      {
        headers: { 
          'Content-Type': 'multipart/form-data',
        }
      }
      );
      const data = response.data;
      setProductInfo(data || 'No product info extracted.');
      
      // Track extraction success
      trackEvent('Product_Info_Extraction_Success', {
        adsType: adsEnum
      });
      mixpanelTrack('product_info_extracted', { adsType: adsEnum });
    } catch (err: any) {
      console.error('Product info extraction error', err);
      setExtractProductInfoError(err.message || 'Extraction failed');
      
      // Track extraction failure
      trackEvent('Product_Info_Extraction_Failed', {
        adsType: adsEnum,
        error: err.message || 'Unknown error'
      });
      trackException(err instanceof Error ? err : new Error(err.message), { 
        context: 'productInfoExtraction',
        adsType: adsEnum 
      });
    } finally {
      setExtractingProductInfo(false);
    }
  };

  const handleCancel = () => {
    abortControllerRef.current?.cancel();
    setLoading(false);
    setProgress(0);
    setProgressStatus('Cancelled');
    
    // Track generation cancellation
    trackEvent('Landing_Page_Generation_Cancelled');
    mixpanelTrack('landing_page_generation_cancelled');
  };

  const handleOpenConfig = () => setShowSettings(true);
  const handleCloseConfig = () => setShowSettings(false);

function detectTranslationCommand(text: string) {
  const sanitizedText = text.normalize('NFKC');
  for (const [, pattern] of Object.entries(translatePatterns)) {
    const match = pattern.exec(sanitizedText);
    if (match) {
      return match[1].trim().replace(/^[\s,.:;!?'"()\-[\]{}]+|[\s,.:;!?'"()\-[\]{}]+$/g, '');
    }
  }
  return null;
}

const extractImageUrlsFromHtml = (html: string): string[] => {
  if (!html?.trim()) return [];
  if (typeof document === 'undefined') return [];

  const container = document.createElement('div');
  container.innerHTML = html;

  const urls = new Set<string>();
  container.querySelectorAll('img').forEach((image) => {
    const src = (image.getAttribute('src') || image.getAttribute('data-src') || '').trim();
    if (src) {
      urls.add(src);
    }
  });

  return Array.from(urls);
};

const publishTemplateToWordPress = async (
  templateData: unknown,
  targetLanguage: string,
) => {
  setProgressStatus('Publishing to WordPress...');
  try {
    const { data } = await axiosInstance.post<{ url: string; title: string; slug: string }>(
      '/api/wordpress/publish',
      {
        templateData,
        productInfo: productDescription,
        targetLanguage,
      },
    );

    toast.success(
      <span>
        Published to WordPress.{' '}
        <a href={data.url} target="_blank" rel="noreferrer">Open page</a>
      </span>,
      { position: 'top-center', autoClose: 10000 },
    );
    trackEvent('WordPress_Publish_Success', { targetLanguage, slug: data.slug });
    return data.url;
  } catch (error: any) {
    toast.error('The template was generated, but WordPress publishing failed.', {
      position: 'top-center',
      autoClose: 7000,
    });
    trackException(error instanceof Error ? error : new Error(String(error)), {
      context: 'wordPressPublish',
      targetLanguage,
    });
    return null;
  }
};

const handleGenerate = async () => {
  const targetLanguage = detectTranslationCommand(productDescription);
  if (targetLanguage) {
    setLoading(true);
    setRegenerableImageUrls([]);
    setRegeneratedImageUrls([]);
    setAcceptedImageRegenerationCount(0);
    await handleTranslate(targetLanguage);
    return;
  }

  const hasProductDescription = productDescription.trim().length > 0;
  const hasTemplateSelection = templateFile !== null;
  const canGenerateWithoutTemplate = scratchModeActive;

  if (!hasProductDescription || (!hasTemplateSelection && !canGenerateWithoutTemplate)) {
    let errorMessage = "";
    if (!hasTemplateSelection && !canGenerateWithoutTemplate) {
      errorMessage += "Template file is required (or enable Generate Template from Scratch). ";
    }
    if (!hasProductDescription) {
      errorMessage += "Product Details is required.";
    }
    showAlert('Error', errorMessage.trim());
    return;
  }

  if (!token) {
    showAlert('Error', 'Not authenticated.');
    return;
  }

  const source = axios.CancelToken.source();
  abortControllerRef.current = source;
  setLoading(true);
  setProgress(0);
  setProgressStatus('Starting...');
  setPreviewHtml('');
  setGeneratedJson(null);
  setKeywords({});
  setProductClassification(null);
  setRegenerableImageUrls([]);
  setRegeneratedImageUrls([]);
  setAcceptedImageRegenerationCount(0);

  // Track generation start
  trackEvent('Landing_Page_Generation_Started', {
    productImageCount: productImages.length,
    hasPrice: !!price,
    hasCurrency: !!currency,
    skipImageGeneration,
    skipWidgets,
    aiModel: settings.aiModel
  });

  // Start progress simulation
  const progressInterval = setInterval(() => {
    setProgress(prev => {
      if (prev >= 95) { // Stop at 95% to allow actual completion to reach 100%
        clearInterval(progressInterval);
        return 95;
      }
      setProgressStatus(`Processing... ${prev + 5}%`);
      return prev + 5;
    });
  }, 9000);

  try {
    const formData = new FormData();
    formData.append('productDescription', productDescription);
    formData.append('price', price);
    formData.append('currency', currency);
    formData.append('currencySymbolPosition', currencySymbolPosition);
    formData.append('skipImageGeneration', skipImageGeneration.toString());
    formData.append('skipWidgets', skipWidgets.toString());
    formData.append('aiModel', settings.aiModel);
    formData.append('useMockSerpAPI', settings.useMockSerpAPI.toString());
    formData.append('isFromScratch', scratchModeActive.toString());

    // In scratch mode, we first ask the backend to generate a fresh Elementor JSON structure,
    // then we pass it into the standard generation pipeline.
    if (fromScratchMode && !generationFromScratchEnabled) {
      showAlert('Error', 'Generate Template from Scratch is currently disabled.');
      return;
    }

    if (scratchModeActive) {
      setProgressStatus('Creating template structure...');

      const structureForm = new FormData();
      structureForm.append('productDescription', productDescription);
      structureForm.append('price', price);
      structureForm.append('currency', currency);
      structureForm.append('currencySymbolPosition', currencySymbolPosition);
      structureForm.append('aiModel', settings.aiModel);

      const structureResponse = await axiosInstance.post<LandingPageGenerateAcceptedResponse>(
        `/api/landingpage/generateStructure`,
        structureForm,
        {
          cancelToken: source.token,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const structureJobId = structureResponse.data?.jobId;
      if (!structureJobId) {
        throw new Error('Structure generation queue did not return a job id.');
      }

      setProgress(5);
      setProgressStatus('Structure generation queued...');

      const templateJsonObject = await pollGenerateStructure(
        structureJobId,
        token,
        source,
        setProgress,
        setProgressStatus,
      );
      const structureString = JSON.stringify(templateJsonObject, null, 2);
      const structureBlob = new Blob([structureString], { type: 'application/json' });
      formData.append('templateJson', structureBlob, 'scratch-template.json');

      // Optional: show structure preview immediately
      try {
        const structureHtml = extractHtmlFromElementorJson(templateJsonObject);
        if (structureHtml) setPreviewHtml(structureHtml);
      } catch {
        // ignore preview failures
      }
    } else if (templateFile?.file) {
      formData.append('templateJson', templateFile.file, templateFile.name);
    }

    // Append multiple product images
    productImages.forEach((img, index) => {
      if (img.file) {
        formData.append('productImages', img.file, img.name);
      }
    });

    setProgressStatus('Uploading data to server...');

    const response = await axiosInstance.post<LandingPageGenerateAcceptedResponse>(
      `/api/landingpage/generate`,
      formData,
      {
        cancelToken: source.token,
        headers: { 
          'Content-Type': 'multipart/form-data',
        }
      }
    );

    const jobId = response.data?.jobId;
    if (!jobId) {
      throw new Error('Generation queue did not return a job id.');
    }

    setProgress(prev => Math.max(prev, 15));
    setProgressStatus('Generation queued...');

    const data = await pollLandingPageGeneration(
      jobId,
      token,
      source,
      setProgress,
      setProgressStatus,
    );


    const jsonString = JSON.stringify(data.generatedJson, null, 2);
    const jsonBlob = new Blob([jsonString], { type: 'application/json' });
    const jsonUrl = URL.createObjectURL(jsonBlob);
    const a = document.createElement('a');
    a.href = jsonUrl;
    a.download = `landing page.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(jsonUrl), 100);

    // Track JSON download
    trackEvent('Landing_Page_JSON_Downloaded');
    mixpanelTrack('landing_page_json_downloaded');

    
    const generatedHtml = extractHtmlFromElementorJson(data.generatedJson);

    const nextRegenerableImageUrlsFromResponse = Array.isArray(data.generatedImageUrls)
      ? data.generatedImageUrls.filter((url: unknown): url is string => typeof url === 'string' && url.trim().length > 0)
      : [];

    const nextRegenerableImageUrls = nextRegenerableImageUrlsFromResponse.length > 0
      ? nextRegenerableImageUrlsFromResponse
      : extractImageUrlsFromHtml(generatedHtml || '');

    setPreviewHtml(generatedHtml || '');
    setRegenerableImageUrls(nextRegenerableImageUrls);
    setRegeneratedImageUrls([]);
    setAcceptedImageRegenerationCount(0);
    setGeneratedJson(data || null);
    setKeywords(data.keywords || {});
    setProductClassification(data.productClassification || null);

    const publishedUrl = publishToWordPress
      ? await publishTemplateToWordPress(data.generatedJson, 'English')
      : null;

    // Complete the progress
    clearInterval(progressInterval);
    setProgress(100);
    setProgressStatus(publishedUrl ? 'Generation complete and published' : 'Generation complete');
    
    // Track generation success
    trackEvent('Landing_Page_Generation_Success', {
      hasKeywords: !!data.keywords,
      hasClassification: !!data.productClassification
    });
    mixpanelTrack('landing_page_generated', {
      has_keywords: !!data.keywords,
      has_classification: !!data.productClassification
    });
  } catch (error: any) {
    // Stop progress simulation on error
    clearInterval(progressInterval);
    if (axios.isCancel(error)) {
      setProgressStatus('Generation cancelled by user');
    } else {
      setProgressStatus('Error');
      
      // Track generation failure
      trackEvent('Landing_Page_Generation_Failed', {
        error: error.message || 'Unknown error',
        errorCode: error.response?.status
      });
      trackException(error instanceof Error ? error : new Error(error.message), { 
        context: 'landingPageGeneration'
      });
    }
  } finally {
    setLoading(false);
    abortControllerRef.current = null;
  }
};
  const handleTranslate = async (targetLanguage: string) => {
    const generatedPayload = generatedJson && (generatedJson as any).generatedJson
      ? (generatedJson as any).generatedJson
      : generatedJson;

    if (!templateFile && !generatedPayload) {
      showAlert('Error', 'Please select a WordPress template JSON file');
      return;
    }

     if (!token) {
      showAlert('Error', 'Not authenticated.');
      return;
    }

    const source = axios.CancelToken.source();
    abortControllerRef.current = source;

    setProgress(5);
    setProgressStatus('Preparing translation request...');
    setLoading(true);

    // Track translation start
    trackEvent('Translation_Started', {
      targetLanguage
    });

    const formData = new FormData();

    if (generatedPayload) {
      const jsonString = JSON.stringify(generatedPayload, null, 2);
      const jsonBlob = new Blob([jsonString], { type: 'application/json' });
      formData.append('templateJson', jsonBlob, 'generated-template.json');
    } else if (templateFile?.file) {
      formData.append('templateJson', templateFile.file, templateFile.name);
    }

    formData.append('price', price);
    formData.append('currency', currency);
    formData.append('currencySymbolPosition', currencySymbolPosition);
    formData.append('targetLanguage', targetLanguage);
    formData.append('skipWidgets', skipWidgets.toString());
    formData.append('aiModel', settings.aiModel);

    try {
      setProgress(15);
      setProgressStatus('Sending translation request...');

      const response = await axiosInstance.post<TranslateAcceptedResponse>(
        `/api/landingpage/translate`,
        formData,
        {
          cancelToken: source.token,
          headers: { 
            'Content-Type': 'multipart/form-data',
          }
        }
      );

      const translateJobId = response.data?.jobId;
      if (!translateJobId) {
        throw new Error('Translation queue did not return a job id.');
      }

      setProgress(20);
      setProgressStatus('Translation queued...');

      const data = await pollTranslate(
        translateJobId,
        token,
        source,
        setProgress,
        setProgressStatus,
      );

      const jsonBlob = new Blob([JSON.stringify(data.generatedJson, null, 2)], { type: 'application/json' });
      const jsonUrl = URL.createObjectURL(jsonBlob);
      const a = document.createElement('a');
      a.href = jsonUrl;
      a.download = `translated-template-${targetLanguage}.json`;
      a.click();
      URL.revokeObjectURL(jsonUrl);

      const generatedHtml = extractHtmlFromElementorJson(data.generatedJson);
      const nextRegenerableImageUrls = extractImageUrlsFromHtml(generatedHtml || '');

      setPreviewHtml(generatedHtml || '');
      setRegenerableImageUrls(nextRegenerableImageUrls);
      setRegeneratedImageUrls([]);
      setAcceptedImageRegenerationCount(0);
      setGeneratedJson(data || null);
      const publishedUrl = publishToWordPress
        ? await publishTemplateToWordPress(data.generatedJson, targetLanguage)
        : null;
      setProgress(100);
      setProgressStatus(
        publishedUrl
          ? `Template translated to ${targetLanguage} and published`
          : `Template translated to ${targetLanguage}`,
      );
      
      // Track translation success
      trackEvent('Translation_Success', {
        targetLanguage
      });
      mixpanelTrack('translation_success', { target_language: targetLanguage });
      
      // Track translation download
      trackEvent('Translation_Downloaded', {
        targetLanguage
      });
      mixpanelTrack('translation_downloaded', { target_language: targetLanguage });
      
    } catch (error: any) {
      if (axios.isCancel(error)) {
        setProgressStatus('Translation cancelled by user');
      } else {
        setProgressStatus('Translation error');
        
        // Track translation failure
        trackEvent('Translation_Failed', {
          targetLanguage,
          error: error.message || 'Unknown error'
        });
        trackException(error instanceof Error ? error : new Error(error.message), { 
          context: 'translation',
          targetLanguage 
        });
      }
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  };

  const normalizeImageUrl = (value: string) => {
    const candidate = (value || '').trim();
    if (!candidate) return '';

    try {
      return new URL(candidate, window.location.href).href;
    } catch {
      return candidate;
    }
  };

  const isSameImageUrl = (left: string, right: string) => {
    const a = normalizeImageUrl(left);
    const b = normalizeImageUrl(right);

    if (!a || !b) return false;
    if (a === b) return true;

    return a.split('?')[0] === b.split('?')[0];
  };

  const replaceImageUrlsInObject = (node: any, originalUrl: string, replacementUrl: string): any => {
    if (node == null) return node;

    if (typeof node === 'string') {
      const normalizedOriginal = normalizeImageUrl(originalUrl);
      const normalizedOriginalNoQuery = normalizedOriginal.split('?')[0];
      let nextValue = node;

      if (node === originalUrl || isSameImageUrl(node, originalUrl)) {
        return replacementUrl;
      }

      if (normalizedOriginal) {
        nextValue = nextValue.split(normalizedOriginal).join(replacementUrl);
      }

      if (normalizedOriginalNoQuery && normalizedOriginalNoQuery !== normalizedOriginal) {
        nextValue = nextValue.split(normalizedOriginalNoQuery).join(replacementUrl);
      }

      return nextValue;
    }

    if (Array.isArray(node)) {
      return node.map(item => replaceImageUrlsInObject(item, originalUrl, replacementUrl));
    }

    if (typeof node === 'object') {
      const updated: Record<string, any> = {};
      Object.entries(node).forEach(([key, value]) => {
        updated[key] = replaceImageUrlsInObject(value, originalUrl, replacementUrl);
      });
      return updated;
    }

    return node;
  };

  const regenerateTemplateImage = async (
    originalImageUrl: string,
    prompt: string,
    referenceImageFiles: File[]
  ): Promise<string> => {
    if (!token) {
      throw new Error('Not authenticated.');
    }

    if (!prompt.trim()) {
      throw new Error('Prompt is required.');
    }

    const formData = new FormData();
    formData.append('prompt', prompt.trim());
    formData.append('originalImageUrl', originalImageUrl);

    referenceImageFiles.forEach(file => {
      formData.append('referenceImages', file, file.name);
    });

    const response = await axiosInstance.post(
      `/api/landingpage/regenerateImage`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    const nextImageUrl = response?.data?.imageUrl;
    if (typeof nextImageUrl !== 'string' || !nextImageUrl.trim()) {
      throw new Error('No image returned from regeneration.');
    }

    mixpanelTrack('template_image_regenerated');
    return nextImageUrl;
  };

  const acceptRegeneratedImage = (originalImageUrl: string, newImageUrl: string) => {
    if (!generatedJson) return;

    const generatedPayload = (generatedJson as any).generatedJson
      ? (generatedJson as any).generatedJson
      : generatedJson;

    const updatedPayload = replaceImageUrlsInObject(generatedPayload, originalImageUrl, newImageUrl);
    const updatedHtml = extractHtmlFromElementorJson(updatedPayload);

    setGeneratedJson((prev) => {
      if (!prev) return prev;
      if ((prev as any).generatedJson) {
        return {
          ...(prev as any),
          generatedJson: updatedPayload,
        };
      }
      return updatedPayload;
    });

    setPreviewHtml(updatedHtml || '');

    setRegenerableImageUrls(prev => prev.filter(url => !isSameImageUrl(url, originalImageUrl)));
    setRegeneratedImageUrls(prev => {
      if (prev.some(url => isSameImageUrl(url, newImageUrl))) return prev;
      return [...prev, newImageUrl];
    });
    setAcceptedImageRegenerationCount(prev => prev + 1);
  };

  const downloadTemplateWithRegeneratedImages = () => {
    if (!generatedJson) {
      showAlert('Error', 'No generated template available to download.');
      return;
    }

    const generatedPayload = (generatedJson as any).generatedJson
      ? (generatedJson as any).generatedJson
      : generatedJson;

    const jsonBlob = new Blob([JSON.stringify(generatedPayload, null, 2)], { type: 'application/json' });
    const jsonUrl = URL.createObjectURL(jsonBlob);
    const a = document.createElement('a');
    a.href = jsonUrl;
    a.download = `landing-page-regenerated-images.json`;
    a.click();
    URL.revokeObjectURL(jsonUrl);
  };

  async function generateMetaAdsContentWithContext(kind: 'main_texts' | 'headlines' | 'descriptions', knowledgeBase: string, productInfo: string): Promise<string[] | null> {
    if (!token) {
      setMetaAdsError('Not authenticated');
      return null;
    }
    setMetaAdsError(null);
    let setFlag: React.Dispatch<React.SetStateAction<boolean>>;
    switch (kind) {
      case 'main_texts': setFlag = setGeneratingMetaMainTexts; break;
      case 'headlines': setFlag = setGeneratingMetaHeadlines; break;
      case 'descriptions': setFlag = setGeneratingMetaDescriptions; break;
    }
    setFlag(true);
    
    // Track Meta Ads generation start
    trackEvent('Meta_Ads_Content_Generation_Started', {
      contentType: kind,
      hasKnowledgeBase: !!knowledgeBase,
      hasProductInfo: !!productInfo
    });
    
    try {
      const formData = new FormData();
      formData.append('ContentType', kind);
      formData.append('KnowledgeBase', knowledgeBase || '');
      formData.append('ProductInformation', productInfo || '');
      const response = await axiosInstance.post(`/api/landingpage/generateMetaContent`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = (response.data as string[]) || [];
      
      // Track Meta Ads generation success
      trackEvent('Meta_Ads_Content_Generation_Success', {
        contentType: kind,
        itemsGenerated: result.length
      });
      mixpanelTrack('meta_ads_content_generated', { content_type: kind, items_generated: result.length });
      
      return result;
    } catch (err: any) {
      console.error('Meta Ads content generation error', err);
      setMetaAdsError(err?.message || 'Meta Ads update failed');
      
      // Track Meta Ads generation failure
      trackEvent('Meta_Ads_Content_Generation_Failed', {
        contentType: kind,
        error: err?.message || 'Unknown error'
      });
      trackException(err instanceof Error ? err : new Error(err?.message), { 
        context: 'metaAdsGeneration',
        contentType: kind
      });
      
      return null;
    } finally {
      setFlag(false);
    }
  }

  async function generateGoogleAdsContentWithContext(kind: 'title' | 'long' | 'desc' | 'term', knowledgeBase: string, productInfo: string): Promise<string[] | null> {
    if (!token) {
      setGoogleAdsError('Not authenticated');
      return null;
    }
    setGoogleAdsError(null);
    let setFlag: React.Dispatch<React.SetStateAction<boolean>>;
    switch (kind) {
      case 'title': setFlag = setGeneratingGoogleTitles; break;
      case 'long': setFlag = setGeneratingGoogleLongTitles; break;
      case 'desc': setFlag = setGeneratingGoogleDescriptions; break;
      case 'term': setFlag = setGeneratingGoogleSearchTerms; break;
    }
    setFlag(true);
    
    // Track Google Ads generation start
    trackEvent('Google_Ads_Content_Generation_Started', {
      contentType: kind,
      hasKnowledgeBase: !!knowledgeBase,
      hasProductInfo: !!productInfo
    });
    
    try {
      const formData = new FormData();
      formData.append('ContentType', kind);
      formData.append('KnowledgeBase', knowledgeBase || '');
      formData.append('ProductInformation', productInfo || '');
      const response = await axiosInstance.post(`/api/landingpage/generateGoogleContent`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const result = (response.data as string[]) || [];
      
      // Track Google Ads generation success
      trackEvent('Google_Ads_Content_Generation_Success', {
        contentType: kind,
        itemsGenerated: result.length
      });
      mixpanelTrack('google_ads_content_generated', { content_type: kind, items_generated: result.length });
      
      return result;
    } catch (err: any) {
      console.error('Google Ads content generation error', err);
      setGoogleAdsError(err?.message || 'Google Ads update failed');
      
      // Track Google Ads generation failure
      trackEvent('Google_Ads_Content_Generation_Failed', {
        contentType: kind,
        error: err?.message || 'Unknown error'
      });
      trackException(err instanceof Error ? err : new Error(err?.message), { 
        context: 'googleAdsGeneration',
        contentType: kind
      });
      
      return null;
    } finally {
      setFlag(false);
    }
  }

  return {
    templateFile,
    setTemplateFile,
    productDescription,
    setProductDescription,
    price,
    setPrice,
    currency,
    setCurrency,
    currencySymbolPosition,
    setCurrencySymbolPosition,
    productImages,
    setProductImages,
    loading,
    setLoading,
    generatedHtml,
    regenerableImageUrls,
    regeneratedImageUrls,
    acceptedImageRegenerationCount,
    setPreviewHtml,
    generatedJson,
    setGeneratedJson,
    keywords,
    setKeywords,
    productClassification,
    setProductClassification,
    progress,
    setProgress,
    progressStatus,
    setProgressStatus,
    skipImageGeneration,
    setSkipImageGeneration,
    skipWidgets,
    setSkipWidgets,
    publishToWordPress,
    setPublishToWordPress,
    wordPressConfigured,
    recentWordPressTemplates,
    selectedWordPressTemplateId,
    setSelectedWordPressTemplateId,
    loadSelectedWordPressTemplate,
    jsonInputRef,
    imageInputRef,
    showSettings,
    handleOpenConfig,
    handleCloseConfig,
    handleFileInput,
    pickTemplateFile,
    handleImagePick,
    handleImageChange,
    handleRemoveImage,
    handleExportKeywords,
    handleCancel,
    handleGenerate,
    regenerateTemplateImage,
    acceptRegeneratedImage,
    downloadTemplateWithRegeneratedImages,
    handleExtractProductInfo,
    extractingProductInfo,
    extractProductInfoError,
    fromScratchMode,
    setFromScratchMode,
    // Meta Ads helpers
    generateMetaAdsContentWithContext,
    generatingMetaMainTexts,
    generatingMetaHeadlines,
    generatingMetaDescriptions,
    generateGoogleAdsContentWithContext,
    generatingGoogleTitles,
    generatingGoogleLongTitles,
    generatingGoogleDescriptions,
    generatingGoogleSearchTerms,
    metaAdsError,
    googleAdsError,
  };
}

type LandingPageGenerationResult = {
  generatedJson: GeneratedJson;
  generatedImageUrls?: string[];
  keywords?: Record<string, string[]>;
  productClassification?: Classification | null;
};

type LandingPageGenerateAcceptedResponse = {
  jobId: string;
  status: string;
  idempotencyKey: string;
};

type LandingPageGenerateResolveResponse = {
  jobId: string;
  status: string;
  result?: LandingPageGenerationResult;
  error?: string;
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

async function pollLandingPageGeneration(
  jobId: string,
  authToken: string,
  source: CancelTokenSource,
  setProgress: Dispatch<SetStateAction<number>>,
  setProgressStatus: Dispatch<SetStateAction<string>>,
): Promise<LandingPageGenerationResult> {
  const startedAt = Date.now();
  const overallTimeoutMs = 12 * 60 * 1000;
  const queueWaitUrl = '/api/landingpage/queue/wait';

  while (Date.now() - startedAt < overallTimeoutMs) {
    if (source.token.reason) {
      throw source.token.reason;
    }

    try {
      const response = await axiosInstance.get<LandingPageGenerateResolveResponse>(
        queueWaitUrl,
        {
          cancelToken: source.token,
          params: { jobId },
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      const status = (response.data?.status || '').toUpperCase();
      if (status === 'COMPLETED') {
        if (!response.data?.result) {
          throw new Error('Generation completed without a result payload.');
        }

        return response.data.result;
      }

      if (status === 'FAILED' || status === 'CANCELLED') {
        throw new Error(response.data?.error || 'Landing page generation failed.');
      }

      setProgress(prev => Math.min(prev < 20 ? 20 : prev + 2, 95));
      setProgressStatus(status === 'PROCESSING' ? 'Awaiting AI response...' : 'Generation queued...');
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const isTransient = !error.response || status === 404 || status === 408 || status === 429 || (status !== undefined && status >= 500);

        if (!isTransient) {
          throw error;
        }

        setProgress(prev => Math.min(prev < 20 ? 20 : prev + 1, 95));
        setProgressStatus('Connecting to generation queue...');
        await delay(2000);
        continue;
      } else {
        throw error;
      }
    }
  }

  throw new Error('Timed out waiting for landing page generation.');
}

type TranslateAcceptedResponse = {
  jobId: string;
  status: string;
  idempotencyKey: string;
};

type TranslateResult = {
  taggedContent: string;
  generatedJson: object;
};

async function pollTranslate(
  jobId: string,
  authToken: string,
  source: CancelTokenSource,
  setProgress: Dispatch<SetStateAction<number>>,
  setProgressStatus: Dispatch<SetStateAction<string>>,
): Promise<TranslateResult> {
  const startedAt = Date.now();
  const overallTimeoutMs = 12 * 60 * 1000;
  const waitUrl = '/api/landingpage/translate/queue/wait';

  while (Date.now() - startedAt < overallTimeoutMs) {
    if (source.token.reason) {
      throw source.token.reason;
    }

    try {
      const response = await axiosInstance.get<{ jobId: string; status: string; result?: TranslateResult; error?: string }>(
        waitUrl,
        {
          cancelToken: source.token,
          params: { jobId },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const status = (response.data?.status || '').toUpperCase();
      if (status === 'COMPLETED') {
        if (!response.data?.result) {
          throw new Error('Translation completed without a result payload.');
        }
        return response.data.result;
      }

      if (status === 'FAILED' || status === 'CANCELLED') {
        throw new Error(response.data?.error || 'Translation failed.');
      }

      setProgress(prev => Math.min(prev < 30 ? 30 : prev + 2, 95));
      setProgressStatus(status === 'PROCESSING' ? 'Translating...' : 'Translation queued...');
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const isTransient = !error.response || status === 404 || status === 408 || status === 429 || (status !== undefined && status >= 500);

        if (!isTransient) {
          throw error;
        }

        setProgress(prev => Math.min(prev < 30 ? 30 : prev + 1, 95));
        setProgressStatus('Connecting to translation queue...');
        await delay(2000);
        continue;
      } else {
        throw error;
      }
    }
  }

  throw new Error('Timed out waiting for translation.');
}

async function pollGenerateStructure(
  jobId: string,
  authToken: string,
  source: CancelTokenSource,
  setProgress: Dispatch<SetStateAction<number>>,
  setProgressStatus: Dispatch<SetStateAction<string>>,
): Promise<object> {
  const startedAt = Date.now();
  const overallTimeoutMs = 12 * 60 * 1000;
  const waitUrl = '/api/landingpage/generatestructure/queue/wait';

  while (Date.now() - startedAt < overallTimeoutMs) {
    if (source.token.reason) {
      throw source.token.reason;
    }

    try {
      const response = await axiosInstance.get<{ jobId: string; status: string; result?: object; error?: string }>(
        waitUrl,
        {
          cancelToken: source.token,
          params: { jobId },
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );

      const status = (response.data?.status || '').toUpperCase();
      if (status === 'COMPLETED') {
        if (!response.data?.result) {
          throw new Error('Structure generation completed without a result payload.');
        }
        return response.data.result;
      }

      if (status === 'FAILED' || status === 'CANCELLED') {
        throw new Error(response.data?.error || 'Structure generation failed.');
      }

      setProgress(prev => Math.min(prev < 20 ? 20 : prev + 2, 40));
      setProgressStatus(status === 'PROCESSING' ? 'Generating template structure...' : 'Structure generation queued...');
    } catch (error) {
      if (axios.isCancel(error)) {
        throw error;
      }

      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const isTransient = !error.response || status === 404 || status === 408 || status === 429 || (status !== undefined && status >= 500);

        if (!isTransient) {
          throw error;
        }

        setProgress(prev => Math.min(prev < 20 ? 20 : prev + 1, 40));
        setProgressStatus('Connecting to structure generation queue...');
        await delay(2000);
        continue;
      } else {
        throw error;
      }
    }
  }

  throw new Error('Timed out waiting for structure generation.');
}